import {useEffect, useRef, useState} from 'react'
import {useMatch, useNavigate, useParams} from 'react-router-dom'
import {useFormik} from 'formik'
import {useTranslation} from 'react-i18next'
import * as Yup from 'yup'

import {Modal} from '../../../../../_metronic/elements/Modal'
import {InputText} from '../../../../../_metronic/elements/Inputs'
import {useErrorHandler} from '../../../../../_metronic/helpers/ErrorHandler'
import {openAlert} from '../../../../../_metronic/elements/NotificationAlert'
import {useMapLayersV2} from '../../Map/MapLayersV2Context'
import {useDrawV2} from '../../Map/Draw/DrawV2Context'
import {useDrawEndButton} from '../../Map/Draw/DrawEndButton/DrawEndButtonContext'
import {DrawTools} from '../../Map/Draw/DrawState'
import {useViewFieldContext} from '../ViewField/ViewFieldContext'
import * as FieldService from '../services/FieldService'
import {fieldPerimeterFromLands} from '../fieldPerimeterFromLands'
import {useSearchContext} from '../../Toolbar/SearchContext'
import {useIsMobile} from '../../../../shared/responsive'
import '../CreateFlow.style.scss'

const ModalHeader = () => {
  const {t} = useTranslation()

  return (
    <div className='modal-header p-5'>
      <h5 className='modal-title'>{t('fields:update.title')}</h5>
    </div>
  )
}

const ModalBody = ({formik, loading, polygonUpdated, updatePolygon, generatePerimeter, loadingLands, field}) => {
  const {t} = useTranslation()

  return (
    <div className='modal-body p-5'>
      <div className='row'>
        <div className='col-lg-12 fv-row'>
          <InputText
            disabled={loading}
            formik={formik}
            name='name'
            inputProps={{
              placeholder: t('fields:create.input.name.placeholder'),
              disabled: loading,
            }}
          />
          <button
            onClick={updatePolygon}
            type='button'
            disabled={loading}
            className={`btn ${polygonUpdated ? 'btn-light-success' : 'btn-light-primary'} mt-5`}
            style={{width: '100%'}}
          >
            {t(`fields:update.buttons.${field?.polygons ? 'update_polygon' : 'add_polygon'}`)}
          </button>
          {!field?.polygons && <button type='button' disabled={loading || loadingLands}
            className='btn btn-light-primary mt-3 w-100' onClick={generatePerimeter}>
            {t('fields:update.buttons.generate_polygon')}
          </button>}
        </div>
      </div>
    </div>
  )
}

const ModalFooter = ({loading, cancel}) => {
  const {t} = useTranslation()

  return (
    <div className='modal-footer'>
      <button disabled={loading === true} type='button' className='btn btn-light' onClick={cancel}>
        {t('fields:update.buttons.cancel')}
      </button>
      <button disabled={loading} type='submit' className='btn btn-primary'>
        {!loading && t('fields:update.buttons.update')}
        {loading && (
          <span className='indicator-progress' style={{display: 'block'}}>
            {t('fields:update.messages.updating')}{' '}
            <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
          </span>
        )}
      </button>
    </div>
  )
}

const MobileUpdateFieldCard = ({
  field,
  formik,
  loading,
  polygonUpdated,
  updatePolygon,
  generatePerimeter,
  loadingLands,
  cancel,
}) => {
  const {t} = useTranslation()

  return (
    <div className='field-view-mobile-shell displayable'>
      <form onSubmit={formik.handleSubmit} className='form'>
        <div className='card shadow-sm right-card-data gis-create-flow-card'>
          <div className='card-header'>
            <p className='gis-create-flow-eyebrow'>{t('fields:labels.field')}</p>
            <h4 className='gis-create-flow-title'>{t('fields:update.title')}</h4>
          </div>

          <div className='card-body'>
            <div className='gis-create-flow-block'>
              <div className='gis-create-flow-input'>
                <p className='gis-create-flow-label'>
                  {t('fields:update.mobile.edit.name_label')}
                </p>
                <InputText
                  disabled={loading}
                  formik={formik}
                  name='name'
                  inputProps={{
                    placeholder: t('fields:create.input.name.placeholder'),
                    disabled: loading,
                  }}
                />
              </div>

              <button
                onClick={updatePolygon}
                type='button'
                disabled={loading}
                className={`btn ${polygonUpdated ? 'btn-light-success' : 'btn-light-primary'} w-100`}
              >
                {t(`fields:update.buttons.${field?.polygons ? 'update_polygon' : 'add_polygon'}`)}
              </button>
              {!field?.polygons && <button type='button' disabled={loading || loadingLands}
                className='btn btn-light-primary mt-3 w-100' onClick={generatePerimeter}>
                {t('fields:update.buttons.generate_polygon')}
              </button>}
            </div>
          </div>

          <div className='card-footer'>
            <button
              disabled={loading === true}
              type='button'
              className='btn btn-light gis-create-flow-footer-button'
              onClick={cancel}
            >
              {t('fields:update.buttons.cancel')}
            </button>
            <button disabled={loading} type='submit' className='btn btn-primary gis-create-flow-footer-button'>
              {!loading && t('fields:update.buttons.update')}
              {loading && (
                <span className='indicator-progress' style={{display: 'block'}}>
                  {t('fields:update.messages.updating')}{' '}
                  <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                </span>
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  )
}

const UpdateField = ({open, setOpen}) => {
  const {field, setField, lands, loadingLands} = useViewFieldContext()
  const [polygonUpdated, setPolygonUpdated] = useState(false)
  const [polygon, setPolygon] = useState(null)
  const [loading, setLoading] = useState(false)
  const submissionPending = useRef(false)
  const drawingPending = useRef(false)
  const {field_id} = useParams()
  const {errorHandler} = useErrorHandler()
  const {t} = useTranslation()
  const mapLayers = useMapLayersV2()
  const drawV2 = useDrawV2()
  const drawEndButton = useDrawEndButton()
  const search = useSearchContext()
  const navigate = useNavigate()
  const isMobile = useIsMobile()
  const routeMatch = useMatch('/gis/field/view/:field_id/update')
  const isRouteDriven = isMobile && routeMatch != null
  const isEditorOpen = isRouteDriven ? !drawV2.isDrawing : open

  const schema = Yup.object().shape({
    name: Yup.string()
      .min(1, t('fields:create.input.name.errors.min'))
      .max(32, t('fields:create.input.name.errors.max'))
      .required(t('fields:create.input.name.errors.required')),
  })

  const formik = useFormik({
    initialValues: {
      name: field?.name || '',
    },
    enableReinitialize: true,
    validationSchema: schema,
    onSubmit: async (values, {setFieldValue}) => {
      if (submissionPending.current || drawingPending.current) return
      submissionPending.current = true
      setLoading(true)

      const extra = {}
      if (polygon?.geometry) {
        extra.polygons = polygon.geometry
      }

      try {
        const res = await errorHandler(FieldService.update(field_id, {name: values.name, ...extra}))

        if (res?.ok) {
          mapLayers.reloadSource()
          search.load()
          setField(res.data)
          openAlert('success', t('fields:update.messages.success_title'), t('fields:update.messages.success'))
          setFieldValue('name', res.data.name)
          resetEditor()
          navigate(`/gis/field/view/${field_id}`)
        }
      } finally {
        submissionPending.current = false
        setLoading(false)
      }
    },
  })

  const reopenEditor = (nextPolygon) => {
    setPolygon(nextPolygon)
    setPolygonUpdated(Boolean(nextPolygon?.geometry))

    if (!isRouteDriven) {
      setOpen(true)
    }
  }

  const startPolygonEdit = (initialGeometry, messageGroup) => {
    if (submissionPending.current || drawingPending.current) return
    drawingPending.current = true
    mapLayers.field.hide(field_id)

    let currentDraft = null
    const rememberFeature = (event) => {
      const feature = event?.features?.[0]
      if (feature?.id != null && feature.geometry) currentDraft = feature
    }

    drawV2.startDrawing({
      tools: [DrawTools.POLYGON],
      onCreate: rememberFeature,
      onUpdate: rememberFeature,
      onDelete: (event) => {
        if (event?.features?.some((feature) => feature.id === currentDraft?.id)) {
          currentDraft = null
        }
      },
    })
    if (initialGeometry) {
      // endDrawing removes its features. Re-add even an already finished draft
      // rather than selecting an ID that no longer exists in Mapbox Draw.
      const id = drawV2.add(initialGeometry)
      currentDraft = {id, geometry: initialGeometry}
      mapLayers.fitToPolygon(initialGeometry)
      drawV2.directSelect(id)
    } else {
      drawV2.mode(DrawTools.POLYGON)
    }

    if (!isRouteDriven) {
      setOpen(false)
    }

    const drawingMessage = messageGroup || (initialGeometry ? 'update_polygon' : 'add_polygon')
    openAlert('info', t(`fields:update.${drawingMessage}.title`), t(`fields:update.${drawingMessage}.message`))

    drawEndButton.show({
      title: t(`fields:update.${drawingMessage}.updated_polygon`),
      buttonText: t('fields:update.buttons.end_edition'),
      onFinish: () => {
        if (!drawingPending.current) return
        const updatedFeature = currentDraft?.id != null ? drawV2.get(currentDraft.id) : null
        drawingPending.current = false
        drawEndButton.hide()
        drawV2.endDrawing()
        mapLayers.field.show(field_id)

        if (!updatedFeature?.geometry) {
          reopenEditor(null)
          return
        }

        reopenEditor({
          id: updatedFeature.id,
          geometry: updatedFeature.geometry,
        })
      },
      onCancel: () => {
        if (!drawingPending.current) return
        drawingPending.current = false
        drawEndButton.hide()
        drawV2.endDrawing()
        setPolygon(null)
        setPolygonUpdated(false)
        mapLayers.field.show(field_id)

        if (!isRouteDriven) {
          setOpen(true)
        }
      },
    })
  }

  const updatePolygon = () => startPolygonEdit(polygon?.geometry || field?.polygons)

  const generatePerimeter = () => {
    if (submissionPending.current || drawingPending.current || loadingLands) return
    try {
      const geometry = fieldPerimeterFromLands(lands)
      startPolygonEdit(geometry, 'generate_polygon')
    } catch (error) {
      const code = ['no_active_lands', 'invalid_land_geometry'].includes(error?.code)
        ? error.code : 'union_failed'
      openAlert('danger', t('fields:update.generate_polygon.title'),
        t(`fields:update.generate_polygon.errors.${code}`))
    }
  }

  const resetEditor = () => {
    drawingPending.current = false
    setPolygonUpdated(false)
    setPolygon(null)
    mapLayers.field.show(field_id)
    drawEndButton.hide()
    drawV2.endDrawing()
    drawV2.deleteAllLayers()
    formik.resetForm()
    if (isRouteDriven) {
      navigate(`/gis/field/view/${field_id}`)
      return
    }

    setOpen(false)
  }

  const cancel = () => {
    if (!submissionPending.current) resetEditor()
  }

  useEffect(() => {
    if (polygon?.geometry) {
      setPolygonUpdated(true)
      return
    }

    setPolygonUpdated(false)
  }, [polygon])

  if (isMobile) {
    if (!isEditorOpen) {
      return null
    }

    return (
      <MobileUpdateFieldCard
        field={field}
        formik={formik}
        loading={loading}
        polygonUpdated={polygonUpdated}
        updatePolygon={updatePolygon}
        generatePerimeter={generatePerimeter}
        loadingLands={loadingLands}
        cancel={cancel}
      />
    )
  }

  return (
    <Modal open={open} setOpen={setOpen}>
      <div className='modal-dialog modal-dialog-centered'>
        <div className='modal-content'>
          <form onSubmit={formik.handleSubmit} className='form'>
            <ModalHeader />
            <ModalBody
              formik={formik}
              field={field}
              loading={loading}
              polygonUpdated={polygonUpdated}
              updatePolygon={updatePolygon}
              generatePerimeter={generatePerimeter}
              loadingLands={loadingLands}
            />
            <ModalFooter loading={loading} cancel={cancel} />
          </form>
        </div>
      </div>
    </Modal>
  )
}

export default UpdateField
